import { Grid, useBoolean } from '@wegroup/design-system';
import { isEmpty } from 'lodash';
import React from 'react';
import { useHistory, useLocation } from 'react-router';
import { useCompanyScanSelector } from '../../common/src/utils/globalVariables';
import CompanyScanContentWrapper from '../../flows/src/components/ui/company-scan/components/app-shell/CompanyScanContentWrapper';
import CompanyScanBottomBar from '../components/bottom-bar/CompanyScanBottomBar';
import usePreviousSession from '../hooks/usePreviousSession';
import { useTranslation } from 'react-i18next';
import usePartyId from '../../common/src/hooks/query-params/usePartyId';
import useSessionId from '../../flows/src/hooks/useSessionId';
import CompanyScanCompanyLookup from '../features/cbe-lookup/CompanyScanCompanyLookup';
import useTracking from '../../common/src/hooks/tracking/useTracking';
import { isSocialLoginUrl } from '../../app/src/features/authentication/constants/SocialAuth';
import { stringify } from 'query-string';
import useQueryParams from '../../common/src/hooks/useQueryParams';

const Lookup: React.FC<React.PropsWithChildren<unknown>> = () => {
  // Hooks
  const { t } = useTranslation();
  const { trackEvent } = useTracking();

  // Routing
  const { pathname, search } = useLocation();
  const history = useHistory();
  const sessionId = useSessionId();
  const queryParams = useQueryParams();

  // State
  const [isStarting, setIsStarting] = useBoolean();

  // Redux
  const partyId =
    usePartyId() ||
    useCompanyScanSelector((state) => state.companyScan.partyId);
  const selectedCompany = useCompanyScanSelector(
    (state) => state.companyScan.selectedCompany,
  );

  // Queries
  const { data: previousSession, isLoading } = usePreviousSession(partyId);

  // Functions
  const handleOnNext = () => {
    setIsStarting.on();
    if (previousSession) {
      history.push({
        pathname: '/company-scan/intro',
        search: stringify(
          {
            session_id: previousSession.id,
            party_id: previousSession.id,
            flowId: queryParams.get('flowId'),
            backRef: pathname,
          },
          { skipEmptyString: true },
        ),
      });
    } else if (selectedCompany?.party_id) {
      trackEvent('Company found in Company Scan');
      history.push({
        pathname: '/company-scan/intro',
        search: stringify(
          {
            party_id: selectedCompany?.party_id,
            flowId: queryParams.get('flowId'),
            backRef: pathname,
          },
          { skipEmptyString: true },
        ),
      });
    } else {
      trackEvent('Company not found in Company Scan');
      history.push({
        pathname: '/company-scan/intro',
        search: stringify(
          {
            flowId: queryParams.get('flowId'),
            backRef: pathname,
          },
          { skipEmptyString: true },
        ),
      });
    }
    setIsStarting.off();
  };

  // Constants
  const isNextButtonDisabled =
    (isEmpty(selectedCompany) && !sessionId) || isLoading || isStarting;

  return (
    <Grid
      templateRows="1fr auto"
      h="100%"
      data-test-id="COMPANY-SCAN_LOOKUP-PAGE_WRAPPER"
    >
      <CompanyScanContentWrapper
        justifyContent="center"
        alignItems="center"
        flexDir="column"
      >
        <CompanyScanCompanyLookup />
      </CompanyScanContentWrapper>
      <CompanyScanBottomBar
        previousText={isSocialLoginUrl ? undefined : t('Go back to dashboard')}
        {...(!isSocialLoginUrl && {
          onPreviousClick: () =>
            history.push({ pathname: '/conversation', search }),
        })}
        isLoadingNext={isLoading || isStarting}
        onNextClick={handleOnNext}
        disabledNext={isNextButtonDisabled}
        nextText={t('NEXT')}
      />
    </Grid>
  );
};

export default Lookup;
